import { SyncOperationText } from "@/components/sync/sync-operation-text";
import { UNTITLED_CONTACT_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { CollectionModelData, CollectionUpsertedSyncUpdateValue } from "@/store/collections/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { IGrantCollectionAclViaSpaceAccountOperation } from "@/store/sync/operations/types";
import {
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
  OptimisticSyncUpdate,
} from "@/store/sync/types";

export class GrantCollectionAclViaSpaceAccountOperation extends BaseSyncOperation<IGrantCollectionAclViaSpaceAccountOperation> {
  private collection?: CollectionObservable;

  get operationKind(): SyncOperationKind {
    return "GRANT_COLLECTION_ACL_VIA_SPACE_ACCOUNT";
  }

  get successToastMessage() {
    return (
      <>
        Sharing {this.mediumTitle} with {this.mediumContactName}...
      </>
    );
  }

  get title() {
    return this.store.collections.get(this.payload.id)?.label;
  }

  get contactName() {
    return (
      this.store.contacts.getBySpaceAccountId(this.payload.space_account_id)?.profileDisplayName ||
      UNTITLED_CONTACT_TITLE
    );
  }

  get mediumTitle() {
    return <SyncOperationText>{this.title}</SyncOperationText>;
  }

  get mediumContactName() {
    return <SyncOperationText>{this.contactName}</SyncOperationText>;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    if (!this.collection || this.collection.isShared) return [];

    const collectionValue: CollectionUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "COLLECTION",
      model_version: this.collection.modelVersion,
      model_data: {
        ...this.collection.modelData,
        shared_at: null, // Don't update shared_at optimistically
      },
      model_scopes: this.collection.modelScopes,
    };
    const collectionUpdate: OptimisticSyncUpdate<CollectionModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: collectionValue,
    };

    return [collectionUpdate];
  }

  async execute() {
    this.collection = await this.store.collections.getAsync(this.payload.id);
    await super.execute();
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.triggerToast(
      <>{this.mediumTitle} could not be shared. If this error continues, please contact support.</>
    );
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerToast(
      <>You do not have permissions to share {this.mediumTitle}. Please contact the note owner.</>
    );
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.triggerToast(
      <>{this.mediumTitle} could not be shared. If this error continues, please contact support.</>,
      SyncErrorHandlingType.RetryWithLimit
    );
  }

  async triggerRecompute() {
    this.store.collections.recompute(this.payload.id);
  }
}
